// frontend/src/components/inventaire/PaymentCreditModal.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import {
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import api from '../../services/auth';
import { formatCurrency, formatDate, formatPhoneNumber } from '../../../src/components/utils/formatters';

const PaymentCreditModal = ({ open, onClose, client, onSuccess }) => {
  const [montant, setMontant] = useState('');
  const [motif, setMotif] = useState('');
  const [modePaiement, setModePaiement] = useState('especes');
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleMontantChange = (e) => {
    const value = e.target.value;
    // Permettre seulement les nombres positifs
    if (value === '' || /^\d+$/.test(value)) {
      setMontant(value);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    // Validation
    const montantNum = parseFloat(montant);
    if (!montant || montantNum <= 0) {
      setError('Le montant doit être supérieur à 0');
      return;
    }

    if (montantNum > client.solde_credit) {
      setError(`Le montant ne peut pas dépasser le solde dû (${formatCurrency(client.solde_credit)})`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`inventory/clients/${client.id}/paiement_credit/`, {
        montant: montantNum,
        motif: motif || 'Paiement crédit client',
        mode_paiement: modePaiement,
        reference: reference || undefined
      });

      setSuccess(true);
      
      // Notifier le parent
      if (onSuccess) {
        onSuccess(response.data);
      }

      // Fermer après 2 secondes
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (err) {
      console.error('Erreur paiement:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Erreur lors du paiement');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setMontant('');
      setMotif('');
      setModePaiement('especes');
      setReference('');
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  const montantNum = parseFloat(montant) || 0;
  const soldeRestant = client.solde_credit - montantNum;
  const creditDisponible = client.credit_disponible;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PaymentIcon color="primary" />
        <Typography variant="h6">
          Paiement crédit - {client.nom_complet || `${client.nom} ${client.prenom || ''}`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {success ? (
          <Alert 
            severity="success" 
            sx={{ mt: 2 }}
            icon={<CheckCircleIcon />}
          >
            Paiement de {formatCurrency(montantNum)} enregistré avec succès !
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {/* Résumé du crédit */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Situation du crédit
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Limite de crédit
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formatCurrency(client.limite_credit)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Solde actuel
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="error.main">
                      {formatCurrency(client.solde_credit)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Crédit disponible
                    </Typography>
                    <Typography variant="body1" color="success.main">
                      {formatCurrency(creditDisponible)}
                    </Typography>
                  </Grid>
                  {montantNum > 0 && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Solde après paiement
                      </Typography>
                      <Typography 
                        variant="body1" 
                        fontWeight="bold"
                        color={soldeRestant > 0 ? 'warning.main' : 'success.main'}
                      >
                        {formatCurrency(soldeRestant)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>

            {/* Montant du paiement */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Montant du paiement"
                value={montant}
                onChange={handleMontantChange}
                required
                error={!!error}
                helperText={error}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">HTG</InputAdornment>
                  ),
                }}
                placeholder="0"
              />
            </Grid>

            {/* Mode de paiement */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={loading}>
                <InputLabel>Mode de paiement</InputLabel>
                <Select
                  value={modePaiement}
                  onChange={(e) => setModePaiement(e.target.value)}
                  label="Mode de paiement"
                >
                  <MenuItem value="especes">Espèces</MenuItem>
                  <MenuItem value="carte">Carte bancaire</MenuItem>
                  <MenuItem value="cheque">Chèque</MenuItem>
                  <MenuItem value="virement">Virement</MenuItem>
                  <MenuItem value="mobile">Mobile money</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Référence (optionnelle) */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Référence"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                disabled={loading}
                placeholder="N° chèque, transaction..."
              />
            </Grid>

            {/* Motif */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Motif (optionnel)"
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                disabled={loading}
                placeholder="Ex: Paiement facture #123"
                multiline
                rows={2}
              />
            </Grid>

            {/* Alerte si le client dépasse sa limite */}
            {client.solde_credit > client.limite_credit && (
              <Grid item xs={12}>
                <Alert severity="warning" icon={<WarningIcon />}>
                  Ce client a dépassé sa limite de crédit de{' '}
                  {formatCurrency(client.solde_credit - client.limite_credit)}.
                </Alert>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {success ? 'Fermer' : 'Annuler'}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading || !montant || parseFloat(montant) <= 0}
            startIcon={loading ? <CircularProgress size={20} /> : <PaymentIcon />}
          >
            {loading ? 'Traitement...' : 'Confirmer le paiement'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentCreditModal;